import type {
  ColumnSeparator,
  FileEncoding,
  TabularFormattedFileParseSettings,
} from '@core/core/types'

export const getSeparatorRegex = (separator: ColumnSeparator) => {
  switch (separator) {
    case 'space':
      return /(?<!^ *) +/
    case 'tab':
      return / *\t */
    case 'comma':
      return / *, */
    case 'colon':
      return / *: */
    case 'semicolon':
      return / *; */
    case 'slash':
      return / *[[/|\\] */
  }
}

function unquote(value: string): string {
  return value.replace(/^["'](.*(?=["']$))["']$/, '$1')
}

export const getSeparatorString = (separator: ColumnSeparator): string => {
  switch (separator) {
    case 'space':
      return ' '
    case 'colon':
      return ':'
    case 'tab':
      return '\t'
    case 'comma':
      return ','
    case 'semicolon':
      return ';'
    case 'slash':
      return '\\'
  }
}

export function parseTableRow(line: string, separator: string): string[] {
  const result: string[] = []
  const escapeChar = '"'
  let isInsideEscape = false
  let currentAcc: string[] = []
  for (const char of line) {
    if (char === escapeChar) {
      isInsideEscape = !isInsideEscape
    }

    if (char === separator && !isInsideEscape && currentAcc.length > 0) {
      result.push(currentAcc.join(''))
      currentAcc = []
    }

    if (char !== separator || isInsideEscape) {
      currentAcc.push(char)
    }
  }

  if (currentAcc.length > 0) {
    result.push(currentAcc.join(''))
  }

  return result.map(value => unquote(value.trim()))
}

export function parseTable(
  file: Blob,
  settings: TabularFormattedFileParseSettings,
): Promise<string[][]> {
  const separator = getSeparatorString(settings.separator)
  const newLine = /\r?\n/
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result
      if (text === null || typeof text !== 'string' || text.length === 0) {
        reject(new Error('blob text reading failed'))
      } else {
        resolve(text)
      }
    }
    reader.readAsText(file, settings.encoding)
  })
    .then(text => text.split(newLine))
    .then(lines => lines.slice(settings.missedLinesCount))
    .then(lines => lines.map(value => parseTableRow(value, separator)))
}

export function parseHeaderLines(file: Blob, encoding: FileEncoding): Promise<string[]> {
  const header = file
  return new Promise<string[]>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result
      if (text === null || typeof text !== 'string' || text.length === 0) {
        reject(new Error('blob text reading failed'))
      } else {
        const newLine = /\r?\n/
        const table = text.split(newLine)
        resolve(table)
      }
    }
    reader.readAsText(header, encoding)
  })
}

export function getDistinctColumnValues(table: string[][], columnNumber: number): string[] {
  const values = table.reduce<Set<string>>((set, row) => {
    const value = row.at(columnNumber - 1)
    if (value) set.add(value)
    return set
  }, new Set<string>())

  return [...values]
}
